import { Box, Grid, HStack, Skeleton, SkeletonCircle, Stack } from '@chakra-ui/react';

/** Hairline between two placeholder cells, as the real card draws it. */
const CELL_DIVIDER = { borderLeftWidth: '1px', borderColor: 'border.subtle' } as const;

/**
 * Loading state of the PROFILE view, drawn on its real layout: the header
 * (avatar, name, handle, badges), the anchor bar, the statistics card (three
 * headline cells over five small ones) and the audience bars. Shown while the
 * view's bundle or its profile slice loads, so the drawer does not flash a
 * partial profile with no-data notices before the measured one lands.
 */
const ContactProfileSkeleton: React.FC = () => (
  <Box aria-busy="true">
    <HStack align="center" gap="15px" px="26px" pt="24px">
      <SkeletonCircle size="58px" flex="none" />
      <Stack gap="2" flex="1">
        <Skeleton h="20px" w="180px" />
        <Skeleton h="13px" w="110px" />
        <HStack gap="6px" mt="4px">
          <Skeleton h="22px" w="104px" rounded="sm" />
          <Skeleton h="22px" w="88px" rounded="sm" />
        </HStack>
      </Stack>
    </HStack>

    <HStack
      gap="22px"
      h="43px"
      mt="18px"
      px="26px"
      borderBottomWidth="1px"
      borderColor="border.subtle"
    >
      {[74, 58, 70, 54, 62].map((width) => (
        <Skeleton key={width} h="12px" w={`${width}px`} />
      ))}
    </HStack>

    <Box px="26px" pt="20px" pb="24px">
      <Box borderWidth="1px" borderColor="border" rounded="lg" overflow="hidden">
        <Grid templateColumns="repeat(3, minmax(0, 1fr))">
          {[0, 1, 2].map((index) => (
            <Stack key={index} gap="8px" p="16px 18px" {...(index > 0 ? CELL_DIVIDER : {})}>
              <Skeleton h="12px" w="60%" />
              <Skeleton h="24px" w="50%" />
            </Stack>
          ))}
        </Grid>
        <Grid
          templateColumns={{ base: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(5, minmax(0, 1fr))' }}
          bg="background"
          borderTopWidth="1px"
          borderColor="border.subtle"
        >
          {[0, 1, 2, 3, 4].map((index) => (
            <Stack
              key={index}
              gap="6px"
              p="11px 14px"
              display={{ base: index > 2 ? 'none' : 'flex', sm: 'flex' }}
              {...(index > 0 ? CELL_DIVIDER : {})}
            >
              <Skeleton h="10px" w="70%" />
              <Skeleton h="14px" w="50%" />
            </Stack>
          ))}
        </Grid>
      </Box>
    </Box>

    <Box px="26px" py="24px" borderTopWidth="1px" borderColor="border.subtle">
      <Skeleton h="15px" w="72px" mb="14px" />
      <Stack gap="14px">
        <Skeleton h="8px" rounded="full" />
        <Skeleton h="8px" rounded="full" />
      </Stack>
      <Skeleton h="58px" rounded="lg" mt="14px" />
    </Box>
  </Box>
);

export default ContactProfileSkeleton;
